import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { checkRateLimit, updateRateLimit, RATE_LIMIT_KEYS } from '../utils/rateLimiter';
import { submitNetlifyForm } from '../utils/netlifyFormSubmit';
import './FeatureRequest.css';

function FeatureRequest() {
  const [submitted, setSubmitted] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestType, setRequestType] = useState('feature');
  const [prefillTitle, setPrefillTitle] = useState('');
  const [prefillEmail, setPrefillEmail] = useState('');

  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      if (location.state.subject) {
        setPrefillTitle(location.state.subject);
      }
      if (location.state.email) {
        setPrefillEmail(location.state.email);
      }
    }
  }, [location]);

  useEffect(() => {
    const timer = setInterval(() => {
      const { remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.FEATURE_REQUEST);
      setRemainingTime(remainingTime);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError('');

    const { allowed, remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.FEATURE_REQUEST);
    if (!allowed) {
      setFormError(`Please wait ${remainingTime} seconds before submitting again.`);
      return;
    }

    const formData = new FormData(e.target);
    const email = String(formData.get('email') || '').trim();
    const description = String(formData.get('description') || '').trim();
    const title = String(formData.get('title') || '').trim();

    if (title.length < 3) {
      setFormError('Title must be at least 3 characters long.');
      return;
    }

    if (description.length < 10) {
      setFormError('Description must be at least 10 characters long.');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setFormError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitNetlifyForm(e.target, 'feature-request');
      updateRateLimit(RATE_LIMIT_KEYS.FEATURE_REQUEST);
      setSubmitted(true);
    } catch (err) {
      setFormError('Failed to send request. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="full-page-wrapper" style={{
        background: 'linear-gradient(to left bottom, #777777, #1a1a1a)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}>
        <div className="success-message">
          <h3>Thank you for your {requestType === 'feature' ? 'feature request' : 'bug report'}!</h3>
          <p>We will review your submission and take appropriate action.</p>
          <button onClick={() => setSubmitted(false)} disabled={remainingTime > 0}>
            {remainingTime > 0
              ? `Wait ${remainingTime}s to submit another request`
              : 'Submit another request'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="full-page-wrapper"
      style={{
        background: 'linear-gradient(to left bottom, #777777, #1a1a1a)',
        width: '100%',
      }}
    >
      <div className="feature-request-form-container">
        <form
          name="feature-request"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="feature-request-form"
        >
          <input type="hidden" name="form-name" value="feature-request" />
          <p hidden>
            <label>
              Do not fill this out if you are human:{' '}
              <input name="bot-field" tabIndex={-1} autoComplete="off" />
            </label>
          </p>

          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label className="radio-group">Request Type:</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="type"
                  value="feature"
                  checked={requestType === 'feature'}
                  onChange={e => setRequestType(e.target.value)}
                />
                Feature Request
              </label>
              <label>
                <input
                  type="radio"
                  name="type"
                  value="bug"
                  checked={requestType === 'bug'}
                  onChange={e => setRequestType(e.target.value)}
                />
                Bug Report
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="title">Title:</label>
            <input
              type="text"
              id="title"
              name="title"
              required
              defaultValue={prefillTitle}
              placeholder={
                requestType === 'feature'
                  ? 'Brief description of the feature'
                  : 'Brief description of the bug'
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input 
              type="email" 
              id="email" 
              name="email" 
              required 
              defaultValue={prefillEmail}
              placeholder="Your email address" 
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description:</label>
            <textarea
              id="description"
              name="description"
              required
              placeholder={
                requestType === 'feature'
                  ? 'Describe the feature you would like to see added...'
                  : 'Describe the bug, including steps to reproduce it...'
              }
            ></textarea>
          </div>

          {requestType === 'bug' && (
            <div className="form-group">
              <label htmlFor="browser">Browser & Version:</label>
              <input
                type="text"
                id="browser"
                name="browser"
                placeholder="e.g., Chrome 91, Firefox 89"
              />
            </div>
          )}

          <button type="submit" disabled={remainingTime > 0 || isSubmitting}>
            {remainingTime > 0
              ? `Wait ${remainingTime}s to submit again`
              : isSubmitting
                ? 'Submitting…'
                : `Submit ${requestType === 'feature' ? 'Feature Request' : 'Bug Report'}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default FeatureRequest;
